import { type EagerResponse, useApolloRequestWrapper } from '../../../api'
import {
  type GetOrderLineListQuery,
  type GetOrderLineListQueryVariables,
  type Instacart_Enterprise_Frigate_Fulfillment_V1_OrderLinesList_OrderItem,
  useGetOrderLineListQuery,
} from '../../../../api'
import { mapMeasuredQuantity } from '../../measuredQuantities'
import { type OrderItem, type OrderLineList, convertMoney } from './types'

export type UseGetOrderLineListVariables = GetOrderLineListQueryVariables

function mapItem(
  item: Instacart_Enterprise_Frigate_Fulfillment_V1_OrderLinesList_OrderItem
): OrderItem {
  return {
    imageUrl: item.imageUrl ?? null,
    name: item.name!,
    productId: Number(item.productId!),
    unitPrice: convertMoney(item.unitPrice!),
    quantity: mapMeasuredQuantity(item.quantity!),
    retailerLookupCode: item.retailerLookupCode ?? null,
    externalData: item.externalData
      ? {
          lineNum: item.externalData.lineNum ?? null,
          retailerUpc: item.externalData.retailerUpc ?? null,
          retailerRrc: item.externalData.retailerRrc ?? null,
          scanCode: item.externalData.scanCode ?? null,
        }
      : null,
    sizeDetails: item.sizeDetails ?? null,
    productCodes: item.productCodes ?? [],
    productCategory: item.productCategory
      ? {
          productCategoryId: item.productCategory.productCategoryId
            ? Number(item.productCategory.productCategoryId)
            : null,
          l1CategoryId: item.productCategory.l1CategoryId
            ? Number(item.productCategory.l1CategoryId)
            : null,
          l2CategoryId: item.productCategory.l2CategoryId
            ? Number(item.productCategory.l2CategoryId)
            : null,
          l3CategoryId: item.productCategory.l3CategoryId
            ? Number(item.productCategory.l3CategoryId)
            : null,
          l4CategoryId: item.productCategory.l4CategoryId
            ? Number(item.productCategory.l4CategoryId)
            : null,
          l5CategoryId: item.productCategory.l5CategoryId
            ? Number(item.productCategory.l5CategoryId)
            : null,
          l6CategoryId: item.productCategory.l6CategoryId
            ? Number(item.productCategory.l6CategoryId)
            : null,
          productCategory: item.productCategory.productCategory ?? null,
          l1Category: item.productCategory.l1Category ?? null,
          l2Category: item.productCategory.l2Category ?? null,
          l3Category: item.productCategory.l3Category ?? null,
          l4Category: item.productCategory.l4Category ?? null,
          l5Category: item.productCategory.l5Category ?? null,
          l6Category: item.productCategory.l6Category ?? null,
        }
      : null,
    alcoholic: Boolean(item.alcoholic),
  }
}

function mapResponse(data: GetOrderLineListQuery): OrderLineList {
  const response =
    data?.instacart_enterprise_frigate_fulfillment_v1_DashboardOrderService_GetOrderLineList
  return {
    results:
      response?.results?.results?.map(result => ({
        id: Number(result.id!),
        creationDate: result.creationDate ?? null,
        status: result.status!,
        item: mapItem(result.item!),
        substitute: result.substitute ? mapItem(result.substitute) : null,
        allowedSubstitution: result.allowedSubstitution ?? null,
        customerSelectedReplacement: result.customerSelectedReplacement
          ? mapItem(result.customerSelectedReplacement)
          : null,
        orderedQuantity: result.orderedQuantity
          ? mapMeasuredQuantity(result.orderedQuantity)
          : null,
        pickedQuantity: result.pickedQuantity ? mapMeasuredQuantity(result.pickedQuantity) : null,
        refundQuantity: result.refundQuantity ? mapMeasuredQuantity(result.refundQuantity) : null,
        fullPrice: convertMoney(result.fullPrice!),
        obfuscatedId: result.obfuscatedId!,
        specialInstructions: result.specialInstructions ?? null,
        substitutionStatus: result.substitutionStatus!,
        customerPrice: convertMoney(result.customerPrice!),
        customerOrderedPrice: convertMoney(result.customerOrderedPrice!),
        replacementPolicy: result.replacementPolicy!,
        addedByDriverId: result.addedByDriverId ? Number(result.addedByDriverId) : null,
      })) ?? [],
  }
}

export const useGetOrderLineList = (
  params: UseGetOrderLineListVariables,
  { shouldSkip = false }: { shouldSkip?: boolean } = {}
): EagerResponse<OrderLineList> => {
  const query = useGetOrderLineListQuery({
    variables: params,
    fetchPolicy: 'cache-first',
    refetchWritePolicy: 'overwrite',
    skip: shouldSkip,
  })
  return useApolloRequestWrapper({ query, mapResponse })
}
